import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { debounceTime, distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';

import { Profile } from '@app/models/account/profile';
import { FilterParameter } from '@summit/models';
import { HistoryService, SummitCrudService } from '@summit/services';

import { interval } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';

@Injectable({
  providedIn: 'root'
})
export class CrudService extends SummitCrudService {
  private preloadWorker: Worker | null = null;
  private preloadWorkerActive = false;
  private preloadWorkerTimeout: any = null;
  private preloadActive = false;
  private preloadAbortControllers: AbortController[] = [];
  private _cancelPreloadSub: (() => void) | null = null;
  protected override activeProfile: Profile;
  protected override baseUrl = 'api/';
  private currentEmployeeId: string | null;

  constructor(
    protected override http: HttpClient,
    protected override authService: AuthenticationService,
    protected override router: Router,
    protected override history: HistoryService
  ) {
    super();
    // Subscribe to active Profile
    // Use distinctUntilChanged to avoid redundant profile processing when object reference changes but id is same
    this.authService.activeProfile$
      .pipe(
        distinctUntilChanged((a, b) => a?.id === b?.id),
        debounceTime(150),
        takeUntil(this.disposing$)
      )
      .subscribe({
        next: (newProfile) => {
          // Cancel any in-flight preloads before switching profile
          this.cancelPreload();
          if (newProfile?.id != this.activeProfile?.id) {
            if (this.activeProfile) {
              this.clear();
            }
            this.activeProfile = newProfile;
            this.currentEmployeeId = newProfile?.id ?? null; //needed for component hash for <Self>
            if (authService.isLoggedIn()) this.preload();
          }
        },
        error: (err) => console.error('Profile Observer got an error: ' + err)
      });
  }

  isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  preload() {
    if (!this.currentEmployeeId) return;
    if (this.preloadActive) {
      this.cancelPreload();
    }
    this.preloadActive = true;
    //console.log(`Creating Array`);
    //HACK DISABLING PRELOAD IN CRUD TO AVOID CIRCULAR DEPENDENCY
    const preloadArray = [
      /*
      {
        schema: new EmployeeSelectSchema(),
        apiFunction: 'Selectable'
      },
      {
        schema: new HubSelectSchema(),
        apiFunction: 'Selectable'
      },
      {
        schema: new RouteSelectSchema(),
        apiFunction: 'Selectable'
      },
      {
        schema: new TruckAliasSchema(),
        apiFunction: 'Selectable'
      }
      */
    ];
    // Use web worker for background preloading
    if (preloadArray.length === 0) return;

    const items = preloadArray.map((entry) => ({
      api: entry.apiFunction ? `${entry.schema.refServerAPI}/${entry.apiFunction}` : entry.schema.refServerAPI,
      filters: entry.schema.refFilterParameters?.map((data) => new FilterParameter(data)) ?? []
    }));

    // Graceful fallback: if Web Workers are not supported, use main-thread idle scheduling
    if (typeof Worker === 'undefined') {
      // fallback: main-thread idle scheduling
      const scheduleIdle = (fn: () => void) => {
        if (typeof (window as any).requestIdleCallback === 'function') {
          (window as any).requestIdleCallback(fn, { timeout: 3000 });
        } else {
          setTimeout(fn, 50);
        }
      };
      scheduleIdle(() => {
        let cancelled = false;
        let completed = 0;
        this.preloadAbortControllers = [];
        const sub = (window as any).rxjs?.interval ? (window as any).rxjs.interval(1750) : interval(1750);
        const obs = sub.pipe(
          // @ts-ignore
          delay(750 + Math.random() * 500),
          take(items.length),
          map((index: number) => items[index])
        );
        obs.subscribe({
          next: (entry: any) => {
            if (cancelled) return;
            let api = entry.api;
            // No filterParameters for fallback
            this.get<any>(api, null, true, []).pipe(take(1), takeUntil(this.disposing$)).subscribe();
          },
          complete: () => {},
          error: () => {}
        });
      });
      return;
    }
    if (!this.preloadWorker) {
      this.preloadWorker = new Worker(new URL('./preload.worker', import.meta.url), { type: 'module' });
    }
    this.preloadWorkerActive = true;
    // Reset/clear any previous timeout
    if (this.preloadWorkerTimeout) {
      clearTimeout(this.preloadWorkerTimeout);
      this.preloadWorkerTimeout = null;
    }
    this.preloadWorker.onmessage = (event: MessageEvent) => {
      const msg = event.data;
      if (msg.type === 'preloadResult') {
        // Optionally handle per-API result
      } else if (msg.type === 'preloadDone') {
        this.preloadWorkerActive = false;
        // Start 2-minute termination timer
        if (this.preloadWorkerTimeout) clearTimeout(this.preloadWorkerTimeout);
        this.preloadWorkerTimeout = setTimeout(
          () => {
            if (this.preloadWorker) {
              this.preloadWorker.terminate();
              this.preloadWorker = null;
              this.preloadWorkerTimeout = null;
            }
          },
          2 * 60 * 1000
        ); // 2 minutes
      } else if (msg.type === 'preloadError') {
        // Optionally log error
      }
    };
    this.preloadWorker.postMessage({
      type: 'startPreload',
      profileId: this.currentEmployeeId,
      items
    });
  }

  private cancelPreload() {
    if (this.preloadWorkerActive && this.preloadWorker) {
      this.preloadWorker.postMessage({ type: 'cancelPreload' });
      this.preloadWorkerActive = false;
    }
    if (this._cancelPreloadSub) {
      this._cancelPreloadSub();
      this._cancelPreloadSub = null;
    }
    this.preloadAbortControllers.forEach((ctrl) => ctrl.abort());
    this.preloadAbortControllers = [];
    this.preloadActive = false;
  }

  public getActiveEmployee(): Profile | null {
    return this.activeProfile;
  }

  override ngOnDestroy(): void {
    // Clean up worker and timer on service destroy (e.g., logout)
    if (this.preloadWorkerTimeout) {
      clearTimeout(this.preloadWorkerTimeout);
      this.preloadWorkerTimeout = null;
    }
    if (this.preloadWorker) {
      this.preloadWorker.terminate();
      this.preloadWorker = null;
    }
    this.preloadWorkerActive = false;
    super.ngOnDestroy();
  }
}
